/* ================================================================================================
 *
 * JS editor component with local running and debugging support.
 * Handing console messages and be able to input custom data in specific function call.
 *
 * TODO:
 *   - Add validation cb for output. (input ports)
 *   - Add validation cb for input. (output ports)
 *   - Check saved js if are valid.
 *   - Add message that the file have saved.
 *   - Indication that we have unsaved modifications in the file (e.x. star in title)
 *
 * ================================================================================================ */
// React components
import * as React from "react";
import _ from "lodash";

// Material UI components
import { Tabs, Tab } from 'material-ui/Tabs';
import IconButton from 'material-ui/IconButton';
import { Toolbar, ToolbarGroup, ToolbarTitle } from 'material-ui/Toolbar';
import { Card, CardHeader, CardText } from 'material-ui/Card';

// Material ICONS
import ContentSaveIcon from 'material-ui/svg-icons/content/save';
import ContentRunIcon from 'material-ui/svg-icons/content/send';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
//import BeautifyIcon from 'material-ui/svg-icons/action/code';

// Others libs
import SplitPane from 'react-split-pane';

// Internal functions
import { IComponentSizeProp } from './Interfaces';
import { Sandbox } from '@Utils/Sandbox/Sandbox';
import { isDefined, isObject, isString } from '@Utils/Micro';
import { MonacoEditor } from './Monaco';
import { ReactJsonView } from './ReactJsonView';
import { Markdown } from './Markdown/MarkdownLazy';

import "./JSEditor.less"
// ================================================================================================
export interface JSEditorProps extends React.Props<JSEditor>, IComponentSizeProp {
    Title?: string;
    JSCode: string;
    InputData?: string;
    HelpMarkdown?: string;
    RunCode?: (
        code: string,
        input: { [name: string]: any },
        onResults: (results: any) => void,
        onError?: (error: string) => void) => boolean;
    onCloseRequest: () => void;
    onSaveRequest: (jsCode: string) => void;
    toolbarHeight: number;
}
// ------------------------------------------------------------------------------------------------
export interface JSEditorState {
    JSCode: string;
    InputData: string;
    OutputData: any;
    InputOutputDataTabCardsOpenNumber: number;
    unsavedChanges: boolean;
    TabIndex: number;
    ConsoleMsg: ConsoleMsgInfo[];
    SplitSize: number;
}
// ------------------------------------------------------------------------------------------------
interface ConsoleMsgInfo {
    Type: "Err" | "Warn" | "Log";
    Msg: string;
    Timestamp: string;
}
// ------------------------------------------------------------------------------------------------
export class JSEditor extends React.Component<JSEditorProps, JSEditorState> {
    private JSRunner: Sandbox;
    constructor(props: JSEditorProps) {
        super(props);

        this.state = {
            JSCode: this.props.JSCode,
            // TODO: pass it from beautify
            InputData: isDefined(this.props.InputData) ? this.props.InputData : "{ }",
            OutputData: {},
            unsavedChanges: false,
            TabIndex: 0,
            ConsoleMsg: [],
            SplitSize: 0.7,
            InputOutputDataTabCardsOpenNumber: 2
        }

        this.JSRunner = new Sandbox("JSEditorRunner");
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const color = "rgb(91, 97, 99)";
        const { toolbarHeight } = this.props;
        let editorWidth = Math.ceil(this.state.SplitSize * this.props.width);
        let editorHeight = this.props.height - toolbarHeight;
        let sideTabsHeight = 52;
        let sideAreaWidth = this.props.width - editorWidth;
        let sideAreaHeight = editorHeight - sideTabsHeight - 2 * 38;
        const disableRipple = false;
        return <div style={{ overflow: "hidden", backgroundColor: "rgb(39, 40, 34)" }}>
            <Toolbar style={{ height: toolbarHeight, boxShadow: "rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px", position: "relative", color: "white", backgroundColor: color, zIndex: 20 }} >
                <ToolbarGroup>
                    <ToolbarTitle style={{ color: "white" }} text={this.props.Title} />
                </ToolbarGroup>
                <ToolbarGroup style={{ color: "white" }} lastChild={true}>
                    <IconButton
                        touch
                        tooltip='Run Code'
                        tooltipPosition='bottom-left'
                        onClick={() => {
                            // Run Code
                            try {
                                var t0 = performance.now();

                                if (isDefined(this.props.RunCode)) {
                                    this.props.RunCode(this.state.JSCode,
                                        JSON.parse(this.state.InputData),
                                        (results) => {
                                            var t1 = performance.now();
                                            this.setState((s: JSEditorState) => {
                                                s.ConsoleMsg.unshift({ Msg: "Executed OK: " + (t1 - t0).toFixed(2) + "ms", Type: "Log", Timestamp: new Date().toLocaleTimeString() });
                                                s.OutputData = results;
                                                return s;
                                            });
                                        },
                                        (error) => {
                                            this.setState((s) => { s.ConsoleMsg.unshift({ Msg: error, Type: "Err", Timestamp: new Date().toLocaleTimeString() }); return s; });
                                        }
                                    );
                                } else {
                                    this.JSRunner.Eval(this.state.JSCode,
                                        JSON.parse(this.state.InputData),
                                        (results) => {
                                            var t1 = performance.now();
                                            this.setState((s: JSEditorState) => {
                                                s.ConsoleMsg.unshift({ Msg: "Executed OK: " + (t1 - t0).toFixed(2) + "ms", Type: "Log", Timestamp: new Date().toLocaleTimeString() });
                                                s.OutputData = results;
                                                return s;
                                            });
                                        },
                                        (error) => {
                                            this.setState((s) => { s.ConsoleMsg.unshift({ Msg: error, Type: "Err", Timestamp: new Date().toLocaleTimeString() }); return s; });
                                        }
                                    );
                                }

                            } catch (ex) {
                                this.setState((s) => { s.ConsoleMsg.unshift({ Msg: ex.message, Type: "Err", Timestamp: new Date().toLocaleTimeString() }); return s; });
                            }
                        }}
                        disableFocusRipple={disableRipple}
                        disableTouchRipple={disableRipple}
                    >
                        <ContentRunIcon color="white" />
                    </IconButton>
                    {
                        /*
                    <IconButton
                        touch
                        tooltip='Beautify Code'
                        tooltipPosition='bottom-left'
                        onClick={() => {
                            try {
                                // TODO: pass it from beautify
                                var newValue = this.state.JSCode;
                                this.setState({ JSCode: newValue })
                            } catch (ex) {
                                console.error(ex);
                            }
                        }}
                    >
                        <BeautifyIcon color="white" />
                    </IconButton>
                    */
                    }
                    <IconButton
                        touch
                        tooltip='Save Changes'
                        tooltipPosition='bottom-left'
                        onClick={() => {
                            if (isDefined(this.props.onSaveRequest))
                                this.props.onSaveRequest(this.state.JSCode);
                        }}
                        disableFocusRipple={disableRipple}
                        disableTouchRipple={disableRipple}
                    >
                        <ContentSaveIcon color="white" />
                    </IconButton>
                    <IconButton
                        touch tooltip='Close' tooltipPosition='bottom-left'
                        onClick={() => {
                            if (isDefined(this.props.onCloseRequest))
                                this.props.onCloseRequest();
                        }}
                        disableFocusRipple={disableRipple}
                        disableTouchRipple={disableRipple}
                    >
                        <CloseIcon color="white" />
                    </IconButton>
                </ToolbarGroup>
            </Toolbar>
            <SplitPane split="vertical"
                style={{ position: "relative" }}
                minSize={250}
                defaultSize={editorWidth}
                onDragFinished={(newSize) => this.setState({ SplitSize: newSize / this.props.width })}
            >
                <MonacoEditor
                    value={this.state.JSCode}
                    defaultTypeString={[
                        'declare var input = {',
                        '    PortState: ' + this.state.InputData + ",",
                        '    ExtraInfo: {},',
                        '    WidgetData: {},',
                        '    ReduxState: {}',
                        '}'
                    ].join('\n')
                    }
                    onChange={(value) => this.setState({ JSCode: value })}
                    width={editorWidth}
                    height={editorHeight}
                    language="javascript"
                    theme="vs-dark"
                    options={{ selectOnLineNumbers: true }}
                />
                <Tabs
                    onChange={(value) => this.setState({ TabIndex: value })}
                    value={this.state.TabIndex}
                    tabItemContainerStyle={{ backgroundColor: color }}
                    style={{ width: sideAreaWidth }}
                    inkBarStyle={{ height: 4 }}
                >
                    <Tab
                        value={0} label={"Input/Output"} >
                        <Card
                            initiallyExpanded={true}
                            style={{ width: sideAreaWidth }}
                            containerStyle={{ padding: 0, paddingBottom: 0 }}
                            onExpandChange={(isExpanded) => {
                                if (isExpanded)
                                    this.setState({ InputOutputDataTabCardsOpenNumber: this.state.InputOutputDataTabCardsOpenNumber + 1 })
                                else
                                    this.setState({ InputOutputDataTabCardsOpenNumber: this.state.InputOutputDataTabCardsOpenNumber - 1 })
                            }}>
                            <CardHeader
                                title="Input"
                                style={{ padding: 8 }}
                                actAsExpander={true}
                                showExpandableButton={true}
                            />
                            <CardText expandable={true} style={{ padding: 0 }}>
                                {
                                    (this.state.InputData == null || isString(this.state.InputData)) ?
                                        <MonacoEditor
                                            value={this.state.InputData}
                                            onChange={(value) => this.setState({ InputData: value })}
                                            width={sideAreaWidth}
                                            height={Math.ceil(sideAreaHeight / this.state.InputOutputDataTabCardsOpenNumber)}
                                            language="json"
                                            theme="vs-dark"
                                            options={{ selectOnLineNumbers: true }}
                                        />
                                        :
                                        null // TODO: have custom editor here
                                }
                            </CardText>
                        </Card>
                        <Card
                            initiallyExpanded={true}
                            style={{ width: sideAreaWidth }}
                            containerStyle={{ padding: 0, paddingBottom: 0 }}
                            onExpandChange={(isExpanded) => {
                                if (isExpanded)
                                    this.setState({ InputOutputDataTabCardsOpenNumber: this.state.InputOutputDataTabCardsOpenNumber + 1 })
                                else
                                    this.setState({ InputOutputDataTabCardsOpenNumber: this.state.InputOutputDataTabCardsOpenNumber - 1 })
                            }}>
                            <CardHeader
                                style={{ padding: 8 }}
                                title="Output"
                                actAsExpander={true}
                                showExpandableButton={true}
                            />
                            <CardText expandable={true} style={{ padding: 0 }}>
                                {
                                    (isObject(this.state.OutputData)) ?
                                        <ReactJsonView
                                            style={{ height: Math.ceil(sideAreaHeight / this.state.InputOutputDataTabCardsOpenNumber), width: sideAreaWidth, overflow: "auto" }}
                                            key={"json-viewer-output"}
                                            src={this.state.OutputData}
                                            theme="monokai"
                                            iconStyle="triangle"
                                            displayDataTypes={false}
                                            displayObjectSize={true}
                                            collapseStringsAfterLength={20}
                                            enableClipboard={true}
                                            indentWidth={4}
                                            collapsed={1}
                                        />
                                        :
                                        <div style={{
                                            backgroundColor: "rgb(39, 40, 34)",
                                            color: "rgb(249, 248, 245)",
                                            height: Math.ceil(sideAreaHeight / this.state.InputOutputDataTabCardsOpenNumber),
                                            width: sideAreaWidth,
                                            fontFamily: "monospace",
                                            letterSpacing: "0.5px",
                                            fontSize: "14px",
                                            overflow: "auto"
                                        }} >
                                            <svg viewBox="0 0 15 15" fill="currentColor" style={{ verticalAlign: "top", color: "rgb(174, 129, 255)", height: "1em", width: "1em", paddingLeft: "2px" }}><path d="M0 14l6-6-6-6z"></path></svg>
                                            {this.state.OutputData}
                                        </div>

                                }
                            </CardText>
                        </Card>
                    </Tab>
                    <Tab value={1} label={"Console"}>
                        <div style={{
                            backgroundColor: "rgb(39, 40, 34)",
                            color: "rgb(249, 248, 245)",
                            width: sideAreaWidth, height: sideAreaHeight + 2 * 38,
                            fontFamily: "monospace",
                            letterSpacing: "0.5px",
                            fontSize: "14px"
                        }}>
                            {
                                _.map(this.state.ConsoleMsg, x => <div style={{ color: (x.Type == "Log") ? "rgb(249, 248, 245)" : "red" }}>
                                    [{x.Timestamp}]
                                    <svg viewBox="0 0 15 15" fill="currentColor" style={{ verticalAlign: "top", color: "rgb(174, 129, 255)", height: "1em", width: "1em", paddingLeft: "2px" }}><path d="M0 14l6-6-6-6z"></path></svg>
                                    {x.Msg}
                                </div>)
                            }
                        </div>
                    </Tab>

                    {
                        (isDefined(this.props.HelpMarkdown)) ?

                            <Tab value={2} label={"Help"}>
                                <div style={{ padding: 10 }}>
                                    <Markdown
                                        style={{ color: "rgb(249, 248, 245)" }}
                                        source={this.props.HelpMarkdown}
                                    />
                                </div>
                            </Tab>
                            : null
                    }
                </Tabs>
            </SplitPane>
        </div>;
    }
    // ---------------------------------------------------------------------------------------------------
}